import React, { useEffect, useContext, useState } from 'react'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import axios from 'axios'
import { Link, Navigate, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Swal from 'sweetalert2'
import AuthContext from '../../../middlewares/AuthContext'
import HomeNav from '../_components/HomeNav'
import Footer from '../_components/Footer'
import {
  GoogleReCaptchaProvider,
  useGoogleReCaptcha
} from 'react-google-recaptcha-v3';
// ── Types ─────────────────────────────────────────────────────────────────────
interface AuthCtx {
  token: string | null
}

// ── Constants ─────────────────────────────────────────────────────────────────
const BRAND        = '#6eb446'
const BRAND_SHADOW = '0 8px 24px rgba(110,180,70,0.3)'

const url = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_URL
  : process.env.REACT_APP_URL_DEV

// ── Password strength indicator ───────────────────────────────────────────────
const checks = [
  { re: /.{8,}/,           label: 'At least 8 characters' },
  { re: /[a-z]/,           label: 'One lowercase letter' },
  { re: /[A-Z]/,           label: 'One uppercase letter' },
  { re: /\d/,              label: 'One number' },
  { re: /[@$!%*#?&.¡,+¿()]+/, label: 'One special character' },
]

const StrengthBar: React.FC<{ password: string }> = ({ password }) => {
  const passed = checks.filter(c => c.re.test(password)).length
  const colors = ['#ef4444', '#f97316', '#eab308', '#84cc16', BRAND]
  const color  = password.length === 0 ? '#e2e8f0' : colors[passed - 1] ?? '#e2e8f0'

  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: 'flex', gap: 4, marginBottom: 6 }}>
        {checks.map((_, i) => (
          <div key={i} style={{
            flex: 1, height: 4, borderRadius: 9999,
            backgroundColor: i < passed ? color : '#e2e8f0',
            transition: 'background-color 0.2s',
          }} />
        ))}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {checks.map((c, i) => (
          <span key={i} style={{
            fontSize: '0.72rem',
            color: c.re.test(password) ? '#166534' : '#94a3b8',
            display: 'flex', alignItems: 'center', gap: 4,
          }}>
            <span>{c.re.test(password) ? '✓' : '○'}</span> {c.label}
          </span>
        ))}
      </div>
    </div>
  )
}

// ── Signup ────────────────────────────────────────────────────────────────────
const Signup: React.FC = () => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { t }              = useTranslation()
  const { token }          = useContext(AuthContext) as AuthCtx
  const navigate           = useNavigate()
  const [loading, setLoading]   = useState(false)
  const [showPass, setShowPass] = useState(false)
  const [showConf, setShowConf] = useState(false)
  const [password, setPassword] = useState('')

  useEffect(() => {
    document.body.classList.add('page-home')
    return () => document.body.classList.remove('page-home')
  }, [])

  if (token) return <Navigate to="/dashboard" replace />

  const formSchema = Yup.object().shape({
    email: Yup.string().email().required(),
    password: Yup.string()
      .min(8, 'At least 8 characters')
      .matches(/[a-z]+/, 'One lowercase character')
      .matches(/[A-Z]+/, 'One uppercase character')
      .matches(/[@$!%*#?&.¡,+¿()]+/, 'One special character')
      .matches(/\d+/, 'One number')
      .required(),
    passwordConfirm: Yup.string()
      .oneOf([Yup.ref('password')], 'Passwords must match')
      .required(),
  })

  const signup = async (params: { email: string; password: string; passwordConfirm: string }) => {
    setLoading(true);
    try {
      if (!executeRecaptcha) {
        console.log('Execute recaptcha not yet available');
        return;
      }

      const token = await executeRecaptcha('token_verify');
      const data = { ...params, token_verify: token } 

      const res = await axios.post(`${url}auth/signup`, data, {
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      })
      Swal.fire({ icon: 'success', title: res.data.response , text: res.data.message })
      navigate('/')
    } catch (err: any) {
      const isWarning = err?.response?.data?.status === 'warning' && err?.response?.data?.message === null
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: isWarning ? err?.response?.data?.response : err?.message,
      })
    } finally {
      setLoading(false)
    }
  }

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px 14px',
    borderRadius: 10,
    border: '1.5px solid #e2e8f0',
    fontSize: '0.9rem',
    color: '#1e293b',
    outline: 'none',
    boxSizing: 'border-box',
    backgroundColor: '#fff',
    transition: 'border-color 0.15s',
  }

  const labelStyle: React.CSSProperties = {
    display: 'block',
    marginBottom: 6,
    fontSize: '0.82rem',
    fontWeight: 600,
    color: '#374151',
  }

  const eyeBtn: React.CSSProperties = {
    position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
    background: 'none', border: 'none', cursor: 'pointer',
    color: '#94a3b8', fontSize: '0.85rem', padding: 4,
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#f8faf6' }}>
      <HomeNav />

      <style>{`
        .su-input:focus {
          border-color: ${BRAND} !important;
          box-shadow: 0 0 0 3px rgba(110,180,70,0.12) !important;
        }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>

      <main style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '3rem 1.5rem' }}>
        <div style={{
          width: '100%',
          maxWidth: 440,
          backgroundColor: '#fff',
          borderRadius: 24,
          boxShadow: '0 8px 40px rgba(0,0,0,0.08)',
          border: '1px solid #f1f5f9',
          overflow: 'hidden',
        }}>

          {/* Top accent */}
          <div style={{ height: 4, backgroundColor: BRAND }} />

          <div style={{ padding: '2.25rem 2rem 2rem' }}>
            {/* Logo + heading */}
            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
              <img
                src="https://labeldictate.com/assets/img/Label-Dictate-Logo-V2021.png"
                width={56}
                alt="Label Dictate"
                style={{ marginBottom: '1rem' }}
              />
              <h1 style={{
                margin: 0,
                fontSize: '1.4rem',
                fontWeight: 800,
                color: '#1e293b',
                letterSpacing: '-0.01em',
              }}>
                {t('header.signup')}
              </h1>
            </div>

            <Formik
              initialValues={{ email: '', password: '', passwordConfirm: '' }}
              validationSchema={formSchema}
              onSubmit={signup}
            >
              {({ values }) => (
                <Form noValidate style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

                  {/* Email */}
                  <div>
                    <label htmlFor="email" style={labelStyle}>{t('login.email')}</label>
                    <Field
                      id="email" name="email" type="email"
                      placeholder="name@example.com"
                      className="su-input"
                      style={inputStyle}
                    />
                    <ErrorMessage name="email" render={msg => (
                      <span style={{ fontSize: '0.78rem', color: '#ef4444', marginTop: 4, display: 'block' }}>{msg}</span>
                    )} />
                  </div>

                  {/* Password */}
                  <div>
                    <label htmlFor="password" style={labelStyle}>{t('login.password')}</label>
                    <div style={{ position: 'relative' }}>
                      <Field
                        id="password" name="password"
                        type={showPass ? 'text' : 'password'}
                        placeholder="••••••••"
                        className="su-input"
                        style={{ ...inputStyle, paddingRight: 42 }}
                        onInput={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                      />
                      <button type="button" onClick={() => setShowPass(v => !v)} style={eyeBtn}>
                        {showPass ? '🙈' : '👁'}
                      </button>
                    </div>
                    <ErrorMessage name="password" render={msg => (
                      <span style={{ fontSize: '0.78rem', color: '#ef4444', marginTop: 4, display: 'block' }}>{msg}</span>
                    )} />
                    {(values.password.length > 0 || password.length > 0) && (
                      <StrengthBar password={values.password} />
                    )}
                  </div>

                  {/* Confirm password */}
                  <div>
                    <label htmlFor="passwordConfirm" style={labelStyle}>{t('login.pass_confirm')}</label>
                    <div style={{ position: 'relative' }}>
                      <Field
                        id="passwordConfirm" name="passwordConfirm"
                        type={showConf ? 'text' : 'password'}
                        placeholder="••••••••"
                        className="su-input"
                        style={{ ...inputStyle, paddingRight: 42 }}
                      />
                      <button type="button" onClick={() => setShowConf(v => !v)} style={eyeBtn}>
                        {showConf ? '🙈' : '👁'}
                      </button>
                    </div>
                    <ErrorMessage name="passwordConfirm" render={msg => (
                      <span style={{ fontSize: '0.78rem', color: '#ef4444', marginTop: 4, display: 'block' }}>{msg}</span>
                    )} />
                  </div>

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={loading}
                    style={{
                      width: '100%',
                      padding: '13px',
                      borderRadius: 9999,
                      border: 'none',
                      backgroundColor: loading ? '#a3d08a' : BRAND,
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: '0.95rem',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      boxShadow: BRAND_SHADOW,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8,
                      marginTop: 4,
                      transition: 'filter 0.15s',
                    }}
                    onMouseEnter={e => { if (!loading) e.currentTarget.style.filter = 'brightness(1.08)' }}
                    onMouseLeave={e => { e.currentTarget.style.filter = 'brightness(1)' }}
                  >
                    {loading && (
                      <span style={{
                        width: 16, height: 16, borderRadius: '50%',
                        border: '2px solid rgba(255,255,255,0.4)',
                        borderTopColor: '#fff',
                        display: 'inline-block',
                        animation: 'spin 0.7s linear infinite',
                      }} />
                    )}
                    {t('header.signup')}
                  </button>

                </Form>
              )}
            </Formik>

            {/* Divider */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '1.5rem 0' }}>
              <div style={{ flex: 1, height: 1, backgroundColor: '#f1f5f9' }} />
              <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>o</span>
              <div style={{ flex: 1, height: 1, backgroundColor: '#f1f5f9' }} />
            </div>

            {/* Login link */}
            <Link
              to="/auth/login"
              style={{
                display: 'block',
                width: '100%',
                padding: '12px',
                borderRadius: 9999,
                border: `1.5px solid ${BRAND}`,
                color: BRAND,
                fontWeight: 600,
                fontSize: '0.9rem',
                textDecoration: 'none',
                textAlign: 'center',
                boxSizing: 'border-box',
                transition: 'background-color 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#f4fbf0' }}
              onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent' }}
            >
              {t('login.login_account')}
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  )
}

export default Signup
